import { useEffect, useState } from "react";
import toast from "../../../utils/toast";
import {
  getAdminProfileData,
  updateAdminProfileData,
  getMasterPincode,
  updateAdminProfilPic,
} from "../../../services/admin/adminServices";
import { useAuth } from "../../../context/AuthContext";

export default function AdminProfile() {
  const {  updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await getAdminProfileData();
      if (res.statusCode === 1) {
        setProfile(res.data);
      }
    } catch (err) {
      console.log("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  // ── Open edit — prefill form from profile ──
  const openEdit = () => {
    setEditForm({
      fullName: profile?.fullName || "",
      whatsApp: profile?.whatsApp || "",
      pincode: profile?.pincode || "",
      district: profile?.district || "",
      state: profile?.state || "",
      address: profile?.address || "",
      landmark: profile?.landmark || "",
      pan: profile?.pan || "",
      aadhaar: profile?.aadhaar || "",
      dob: profile?.dob ? profile.dob.split("T")[0] : "",
      bankName: profile?.bankName || "",
      ifsc: profile?.ifsc || "",
      accountNumber: profile?.accountNumber || "",
      accountHolder: profile?.accountHolder || "",
      fatherName: profile?.fatherName || "",
      occupation: profile?.occupation || "",
      company: profile?.company || "",
      permanentAddress: profile?.permanentAddress || "",
    });
    setEditErrors({});
    setIsEditing(true);
  };

  const closeEdit = () => {
    setIsEditing(false);
    setEditForm({});
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePincodeChange = async (e) => {
    handleEditChange(e);
    const val = e.target.value;
    if (val.length === 6) {
      try {
        const res = await getMasterPincode(val);
        if (res.statusCode === 1) {
          setEditForm((prev) => ({
            ...prev,
            district: res.data.district,
            state: res.data.stateName,
          }));
        }
      } catch (err) {
        console.log("Pincode not found");
      }
    } else {
      setEditForm((prev) => ({ ...prev, district: "", state: "" }));
    }
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.fullName) e.fullName = "Full name is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdateProfile = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        ...editForm,
        dob: editForm.dob ? new Date(editForm.dob).toISOString() : null,
      };
      const res = await updateAdminProfileData(payload);
      if (res.statusCode === 1) {
        updateUser({ fullName: editForm.fullName });
        toast.success(res.message);
        closeEdit();
        fetchProfile();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const res = await updateAdminProfilPic(file);
      if (res.statusCode === 1) {
        updateUser({ profilePhoto: res.data });
        toast.success(res.message);
        fetchProfile();
      } else {
        toast.error(res.message || "Failed to upload photo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const initials = profile?.fullName
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  if (loading) {
    return (
      <div className="main">
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "var(--text-tertiary)",
          }}
        >
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">My Profile</div>
          <div className="page-sub">Admin › My Profile</div>
        </div>

        <div className="topbar-right">
          <button className="btn btn-primary" onClick={openEdit}>
            ✎ Edit Profile
          </button>
        </div>
      </div>

      <div className="content">
        <div className="card" style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            {/* hidden file input */}
            <input
              type="file"
              id="profile-pic-input"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleProfilePicChange}
            />

            {/* clickable avatar */}
            <div
              onClick={() =>
                document.getElementById("profile-pic-input").click()
              }
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: "rgb(181, 212, 244)",
                color: "rgb(12, 68, 124)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                fontWeight: 700,
                flexShrink: 0,
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
              }}
              title="Click to change photo"
            >
              {profile?.profilePhoto ? (
                <img
                  src={profile.profilePhoto}
                  alt="profile"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : uploading ? (
                "..."
              ) : (
                initials || "SA"
              )}

              {/* hover overlay */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  fontSize: 18,
                  color: "#fff",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
              >
                📷
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                {profile?.fullName || "—"}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  fontSize: 13,
                  color: "var(--text-secondary)",
                }}
              >
                <span>📧 {profile?.email || "—"}</span>
                <span>📱 {profile?.mobile || "—"}</span>
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <span className="badge badge-active">
                  {profile?.roleName || "—"}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--text-tertiary)",
                    alignSelf: "center",
                  }}
                >
                  Last login: {formatDateTime(profile?.lastLoginAt)}
                </span>
              </div>
            </div>
            <div
              style={{
                textAlign: "right",
                fontSize: 12,
                color: "var(--text-tertiary)",
              }}
            >
              <div>Member since</div>
              <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {formatDate(profile?.createdAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="two-col">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="card">
              <div className="card-title">Basic Information</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  rowGap: 10,
                }}
              >
                {[
                  ["Full Name", profile?.fullName],
                  ["Email", profile?.email],
                  ["Mobile", profile?.mobile],
                  ["WhatsApp", profile?.whatsApp],
                  ["Date of Birth", formatDate(profile?.dob)],
                  ["Father Name", profile?.fatherName],
                  ["Occupation", profile?.occupation],
                  ["Company", profile?.company],
                ].map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-title">Bank Details</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  rowGap: 10,
                }}
              >
                {[
                  ["Bank Name", profile?.bankName],
                  ["IFSC Code", profile?.ifsc],
                  ["Account Number", profile?.accountNumber],
                  ["Account Holder", profile?.accountHolder],
                ].map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="card">
              <div className="card-title">Address</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  rowGap: 10,
                }}
              >
                {[
                  ["Address", profile?.address],
                  ["Permanent Address", profile?.permanentAddress],
                  ["Landmark", profile?.landmark],
                  ["District", profile?.district],
                  ["State", profile?.state],
                  ["Pincode", profile?.pincode],
                ].map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-title">KYC Information</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  rowGap: 10,
                }}
              >
                {[
                  ["PAN", profile?.pan],
                  ["Aadhaar", profile?.aadhaar],
                  ["KYC Status", profile?.kycStatus],
                ].map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-title">Wallet</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "130px 1fr",
                  rowGap: 10,
                }}
              >
                <Row
                  label="Wallet Balance"
                  value={
                    profile?.walletBalance != null
                      ? `₹${profile.walletBalance.toLocaleString("en-IN")}`
                      : null
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Profile Modal ── */}
      {isEditing && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 700 }}>
            <div className="modal-header">
              <div className="modal-title">Edit Profile</div>
              <button className="modal-close" onClick={closeEdit}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Basic Info */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Basic Information
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={editForm.fullName}
                    onChange={handleEditChange}
                    placeholder="Enter full name"
                    style={
                      editErrors.fullName ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {editErrors.fullName && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.fullName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>WhatsApp Number</label>
                  <input
                    type="tel"
                    name="whatsApp"
                    value={editForm.whatsApp}
                    onChange={handleEditChange}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={editForm.dob}
                    onChange={handleEditChange}
                  />
                </div>
                <div className="form-group">
                  <label>Father Name</label>
                  <input
                    type="text"
                    name="fatherName"
                    value={editForm.fatherName}
                    onChange={handleEditChange}
                    placeholder="Father's name"
                  />
                </div>
                <div className="form-group">
                  <label>Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={editForm.occupation}
                    onChange={handleEditChange}
                    placeholder="Your occupation"
                  />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="company"
                    value={editForm.company}
                    onChange={handleEditChange}
                    placeholder="Company name"
                  />
                </div>
              </div>

              <hr
                style={{
                  border: "none",
                  borderTop: "0.5px solid var(--border-light)",
                  margin: "14px 0",
                }}
              />

              {/* Address */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Address
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={editForm.pincode}
                    onChange={handlePincodeChange}
                    placeholder="6-digit pincode"
                    maxLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    name="district"
                    value={editForm.district}
                    onChange={handleEditChange}
                    placeholder="District"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={editForm.state}
                    onChange={handleEditChange}
                    placeholder="State"
                  />
                </div>
                <div className="form-group">
                  <label>Landmark</label>
                  <input
                    type="text"
                    name="landmark"
                    value={editForm.landmark}
                    onChange={handleEditChange}
                    placeholder="Near landmark"
                  />
                </div>
              </div>
              <div className="form-group full">
                <label>Address</label>
                <textarea
                  name="address"
                  value={editForm.address}
                  onChange={handleEditChange}
                  placeholder="Complete address"
                  style={{ minHeight: 52 }}
                />
              </div>
              <div className="form-group full">
                <label>Permanent Address</label>
                <textarea
                  name="permanentAddress"
                  value={editForm.permanentAddress}
                  onChange={handleEditChange}
                  placeholder="Permanent address"
                  style={{ minHeight: 52 }}
                />
              </div>

              <hr
                style={{
                  border: "none",
                  borderTop: "0.5px solid var(--border-light)",
                  margin: "14px 0",
                }}
              />

              {/* KYC */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                KYC Information
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>PAN</label>
                  <input
                    type="text"
                    name="pan"
                    value={editForm.pan}
                    onChange={handleEditChange}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
                <div className="form-group">
                  <label>Aadhaar</label>
                  <input
                    type="text"
                    name="aadhaar"
                    value={editForm.aadhaar}
                    onChange={handleEditChange}
                    placeholder="XXXX XXXX XXXX"
                    maxLength={12}
                  />
                </div>
              </div>

              <hr
                style={{
                  border: "none",
                  borderTop: "0.5px solid var(--border-light)",
                  margin: "14px 0",
                }}
              />

              {/* Bank Details */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-tertiary)",
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Bank Details
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    type="text"
                    name="bankName"
                    value={editForm.bankName}
                    onChange={handleEditChange}
                    placeholder="Bank name"
                  />
                </div>
                <div className="form-group">
                  <label>IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc"
                    value={editForm.ifsc}
                    onChange={handleEditChange}
                    placeholder="SBIN0012345"
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={editForm.accountNumber}
                    onChange={handleEditChange}
                    placeholder="Account number"
                  />
                </div>
                <div className="form-group">
                  <label>Account Holder</label>
                  <input
                    type="text"
                    name="accountHolder"
                    value={editForm.accountHolder}
                    onChange={handleEditChange}
                    placeholder="As per bank records"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEdit}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateProfile}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <span
        style={{
          fontSize: 12,
          color: "var(--text-tertiary)",
          alignSelf: "start",
          paddingTop: 1,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: value ? "var(--text-primary)" : "var(--text-tertiary)",
          fontWeight: value ? 500 : 400,
        }}
      >
        {value || "—"}
      </span>
    </>
  );
}
